import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import clsx from "clsx";
import styles from "./TouchLoupe.module.scss";

/* UI-DESIGN: LB-52 the magnifier on phones. Phones show pre-rendered frames
   instead of the live scene, so the scene's loupe never reaches them; this
   is the same steel lens drawn over the frame image. It floats above the
   finger so the finger never covers what it magnifies.
   LB-71 (Taco, iPhone pass 2026-10-16): press and hold the file to lift the
   lens, drag to move it, let go and it is gone. Nothing to dismiss, and a
   normal swipe on the image still scrolls the page. */
const ZOOM = 2;
const LIFT = 76; // lens centre above the finger, px
const TAP = 10; // movement under this before the hold fires still counts as holding, px
const HOLD = 280; // press-and-hold time before the lens appears, ms

type Point = { x: number; y: number };
type TouchLoupeProps = {
  src: string;
  className?: string;
  imgClassName?: string;
};

const TouchLoupe = ({ src, className, imgClassName }: TouchLoupeProps) => {
  const wrap = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<Point | null>(null);
  const [size, setSize] = useState<Point>({ x: 0, y: 0 });
  const start = useRef<Point | null>(null);
  const last = useRef<Point>({ x: 0, y: 0 });
  const timer = useRef<number | undefined>(undefined);
  const active = useRef(false);

  const local = (x: number, y: number): Point => {
    const r = wrap.current!.getBoundingClientRect();
    setSize({ x: r.width, y: r.height });
    return {
      x: Math.min(Math.max(x - r.left, 0), r.width),
      y: Math.min(Math.max(y - r.top, 0), r.height),
    };
  };

  const stop = () => {
    window.clearTimeout(timer.current);
    start.current = null;
    active.current = false;
    setAt(null);
  };

  /* UI-DESIGN: while the lens is up, the finger moves it rather than the
     page. iOS only honours this from a non-passive touchmove listener. */
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const hold = (e: TouchEvent) => {
      if (active.current) e.preventDefault();
    };
    el.addEventListener("touchmove", hold, { passive: false });
    return () => {
      el.removeEventListener("touchmove", hold);
      window.clearTimeout(timer.current);
    };
  }, []);

  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    start.current = last.current = { x: e.clientX, y: e.clientY };
    const id = e.pointerId;
    const target = e.currentTarget;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (!start.current) return;
      active.current = true;
      try {
        target.setPointerCapture(id);
      } catch {
        /* pointer already released */
      }
      setAt(local(last.current.x, last.current.y));
    }, HOLD);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    last.current = { x: e.clientX, y: e.clientY };
    if (active.current) {
      setAt(local(e.clientX, e.clientY));
    } else if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) >= TAP) {
      /* a swipe, not a hold: let the page scroll */
      window.clearTimeout(timer.current);
      start.current = null;
    }
  };

  return (
    <div
      ref={wrap}
      className={clsx(styles.wrap, at && styles.active, className)}
      data-lb-touch-loupe={at ? "on" : ""}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img className={imgClassName} src={src} alt="" loading="lazy" decoding="async" draggable={false} />
      {at ? (
        <div
          className={styles.lens}
          aria-hidden
          style={{
            transform: `translate(${at.x}px, ${at.y - LIFT}px)`,
            backgroundImage: `url(${src})`,
            backgroundSize: `${size.x * ZOOM}px ${size.y * ZOOM}px`,
            ["--lb-at-x" as string]: `${-at.x * ZOOM}px`,
            ["--lb-at-y" as string]: `${-at.y * ZOOM}px`,
          }}
        >
          <span className={styles.handle} />
        </div>
      ) : null}
    </div>
  );
};

export default TouchLoupe;
